import { FormEvent, FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Alert, Spinner } from 'react-bootstrap'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import * as api from './api.client'
import { HospitalDoctor } from './types'
import { removeAlphabeticCharacteres } from '../../utilities/string.util'
import { AppMessage } from '../../utilities/messages'
import { useHospitalAdmin } from './hospitalAdminContext'
import ManagedHospitalSelect from './components/managedHospitalSelect'
import DoctorForm, { DoctorFormValues } from './components/doctorForm'

const emptyDoctor: HospitalDoctor = {
  id: 0,
  name: '',
  email: '',
  cpf: '',
  phoneNumber: '',
}

const EditDoctor: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { selectedHospital, requiresHospitalSelection } = useHospitalAdmin()
  const [doctor, setDoctor] = useState<HospitalDoctor>(emptyDoctor)
  const [loading, setLoading] = useState(true)
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  useEffect(() => {
    const loadDoctor = async () => {
      if (!selectedHospital) {
        setLoading(false)
        return
      }

      try {
        const doctors = await api.getDoctors(selectedHospital.hospitalId)
        const found = doctors.find(
          (item) => item.id === Number.parseInt(id as string)
        )

        if (!found) {
          setAppMessage({
            show: true,
            success: false,
            message: 'Médico não encontrado.',
          })
          return
        }

        setDoctor(found)
      } catch (error) {
        setAppMessage({
          show: true,
          success: false,
          message: `Erro ao carregar o médico: ${(error as any).messages.join(
            ', '
          )}`,
        })
      } finally {
        setLoading(false)
      }
    }

    loadDoctor()
  }, [id, selectedHospital])

  const updateDoctor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    if (!selectedHospital) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Selecione o hospital antes de atualizar o médico.',
      })
      return
    }

    setSaving(true)

    try {
      await api.updateDoctor(selectedHospital.hospitalId, doctor.id, {
        name: doctor.name,
        email: doctor.email,
        cpf: removeAlphabeticCharacteres(doctor.cpf),
        phoneNumber: removeAlphabeticCharacteres(doctor.phoneNumber),
      })

      navigate(`/doctors`, {
        state: {
          doctorUpdated: true,
        },
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: `Erro ao atualizar o médico: ${(error as any).messages.join(
          ', '
        )}`,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = (
    field: keyof DoctorFormValues,
    value: string | boolean
  ) => setDoctor((current) => ({ ...current, [field]: value }))

  return (
    <Page>
      <PageHeader
        back={{ to: '/doctors', label: 'Médicos' }}
        title="Editar médico"
      />

      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <>
          {requiresHospitalSelection && (
            <Alert variant="primary">
              <p className="mb-2">Selecione o hospital para editar o médico.</p>
          <ManagedHospitalSelect />
            </Alert>
          )}

          {appMessage.show && (
            <Alert
              variant="danger"
              onClose={() => setAppMessage({ show: false })}
              dismissible
            >
              {appMessage.message}
            </Alert>
          )}

          <DoctorForm
            values={doctor}
            onChange={handleFormChange}
            onSubmit={updateDoctor}
            onCancel={() => navigate('/doctors')}
            validated={showFormErrors}
            saving={saving}
          />
        </>
      )}
    </Page>
  )
}

export default EditDoctor
